import NeptuneElement from "../../NeptuneElement.js";

class RadioButton extends NeptuneElement {

    templateMarkup = `
        <div>
            <input type="radio" />
            <label></label>
        </div>
    `;

    componentCssUrl = '/src/components/form/radioButton/radio-button.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        
        if (!this.hasAttribute('name')) {
            throw new Error('neptune-radio-button: "name" attribute is required.');
        }
        
        this.processInitialAttributesAfterRender(RadioButton.observedAttributes);
        this.setupAccessibility();
        this.setEventListeners();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['name', 'value', 'checked', 'disabled', 'label', 'required', 'autocomplete'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        
        if (!this.isRendered) {
            return;
        }
        
        this.processAttribute(name, oldValue, newValue);
    }   

    setupAccessibility() {
        const radioInput = this.shadowRoot.querySelector('input[type="radio"]');
        const label = this.shadowRoot.querySelector('label');
        
        if (!radioInput || !label) return;

        const uniqueId = `neptune-radio-${Math.random().toString(36).substr(2, 9)}`;
        
        radioInput.id = uniqueId;
        label.setAttribute('for', uniqueId);
    }

    processAttribute(name, oldValue, newValue) {
        const radioInput = this.shadowRoot.querySelector('input[type="radio"]');
        if(!radioInput) {
            return;
        }

        switch (name) {
            case 'name': {
                if(newValue) {
                    radioInput.name = newValue;
                } else {
                    radioInput.removeAttribute('name');
                    throw new Error('neptune-radio-button: "name" attribute is required.');
                }
                break;
            }
            case 'value': {
                if(newValue) {
                    radioInput.value = newValue;
                } else {
                    radioInput.removeAttribute('value');
                }
                break;
            }
            case 'checked': {
                const isChecked = this.hasAttribute('checked');
                radioInput.checked = isChecked;
                break;
            }
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                radioInput.disabled = isDisabled;
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? radioInput.setAttribute('required', '')
                    : radioInput.removeAttribute('required');
                break;
            }
            case 'autocomplete': {
                const autocomplete = this.getAttribute('autocomplete');
                autocomplete !== null 
                    ? radioInput.setAttribute('autocomplete', autocomplete) 
                    : radioInput.setAttribute('autocomplete', 'off');
                break;
            }
            case 'label': {
                const labelSpan = this.shadowRoot.querySelector('label');
                if(newValue) {
                    labelSpan.textContent = newValue;
                    labelSpan.style.display = 'inline';
                } else {
                    labelSpan.textContent = '';
                    labelSpan.style.display = 'none';
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    setEventListeners() {
        this.removeEventListeners();
        const radioInput = this.shadowRoot.querySelector('input[type="radio"]');
        if(!radioInput) {
            return;
        }

        this.handleRadioChange = (e) => {
            this.setAttribute('checked', '');

            this.dispatchEvent(new CustomEvent('neptune-radio-select', {
                bubbles: true,
                composed: true,
                detail: {
                    value: this.getAttribute('value'),
                    name: this.getAttribute('name')
                }
            }));
        };

        radioInput.addEventListener('change', this.handleRadioChange);
    }
    
    removeEventListeners() {
        const radioInput = this.shadowRoot.querySelector('input[type="radio"]');
        if(radioInput){
            radioInput.removeEventListener('change', this.handleRadioChange);
        }
    }
}

customElements.define('neptune-radio-button', RadioButton);

export default RadioButton;